import * as DG from "ui/dialogs"
import {
  ChangeEvent,
  FocusEventHandler,
  useCallback,
  useEffect,
  useState,
  MouseEvent as ReactMouseEvent,
} from "react"

import {
  BroadcastHours,
  Exclusion,
  HourInterval,
  PackageItem,
} from "models/packages"
import { CellProps, Col } from "components/Rable/types"
import {
  DayOfWeek,
  DaysOfWeek,
} from "routes/Base/Plans/Details/SpotsPlan/Repeat/DaysOfWeek"
import { css, cx } from "@emotion/css"

import Button from "ui/buttons/Button"
import { Exclusions } from "./Exclusions"
import GhostTextInput from "ui/inputs/GhostTextInput"
import { HourRangePicker } from "components/HourPicker/HourRangePicker"
import IconButton from "ui/buttons/IconButton"
import Options from "components/inputs/Options"
import { ReactComponent as PriceTableIcon } from "assets/icon-pricetable.svg"
import get from "lodash/get"
import isEqual from "lodash/isEqual"
import set from "lodash/fp/set"
import { useI18n } from "providers/I18n"

type Props = {
  onClose: () => void
  data?: BroadcastHours
  onUpdate: (rowId: string, values: Partial<PackageItem>) => void
  rowId: string
  open: boolean
  allowed?: boolean
}

export function BroadcastHoursModal(props: Props) {
  const { t } = useI18n()
  const { onClose, data, onUpdate, rowId, open, allowed } = props
  const { days, hours, exclusions } = data ?? {}

  const [selectedDays, setSelectedDays] = useState<string[]>(days || [])
  const [hourInterval, setHourInterval] = useState<HourInterval | undefined>(
    hours,
  )
  const [selectedExclusions, setSelectedExclusions] = useState<{
    exclusions?: Exclusion[]
    checked: boolean
  }>(() => ({
    exclusions,
    checked: !!exclusions?.length,
  }))

  useEffect(() => {
    setSelectedDays(days || [])
    setHourInterval(hours)
    setSelectedExclusions({
      exclusions,
      checked: !!exclusions?.length,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const isDaySelected = useCallback(
    (day: DayOfWeek) => selectedDays?.includes(day),
    [selectedDays],
  )

  const onDayChange = useCallback(
    (day: DayOfWeek) => {
      if (selectedDays?.includes(day)) {
        setSelectedDays((s) => s?.filter((d) => d !== day))
      } else {
        setSelectedDays((d) => [...d, day])
      }
    },
    [selectedDays],
  )

  const onTimeBeltChange = ({
    name,
    value,
  }: {
    name: string
    value?: string
  }) => {
    setHourInterval((s) => ({ ...s, [name]: value }))
  }

  const onExclusionsChange = (name?: string, value?: string) => {
    !!name
      ? setSelectedExclusions((exc) => ({
          ...exc,
          exclusions: set(name, value, exc?.exclusions || []),
        }))
      : setSelectedExclusions((exc) => ({
          ...exc,
          exclusions: [...(exc?.exclusions || []), {}],
        }))
  }

  const onExclusionsCheck = (checked: boolean) => {
    setSelectedExclusions((exc) => ({ ...exc, checked }))
  }

  const onExclusionsRemove = (
    _: ReactMouseEvent<HTMLButtonElement>,
    idx: number,
  ) => {
    setSelectedExclusions((exc) => {
      let updatedExclusions = [...(exc?.exclusions || [])]
      updatedExclusions?.splice(idx, 1)
      return {
        ...exc,
        exclusions: !!updatedExclusions.length ? updatedExclusions : [{}],
      }
    })
  }

  const handleClose = () => {
    setSelectedDays(days || [])
    setHourInterval(hours)
    setSelectedExclusions({
      exclusions,
      checked: !!exclusions?.length,
    })
    onClose()
  }

  const handleSubmit = () => {
    const exclusionsParsed =
      selectedExclusions?.exclusions?.filter(
        (e) => !!e?.day || !!e?.start || !!e?.end,
      ) || []
    const excToSubmit = selectedExclusions?.checked ? exclusionsParsed : []
    const hasDiffs =
      !isEqual(selectedDays, days) ||
      !isEqual(hourInterval, hours) ||
      !isEqual(excToSubmit, exclusions)
    hasDiffs &&
      onUpdate(rowId, {
        broadcastHours: {
          days: selectedDays,
          hours: hourInterval,
          exclusions: excToSubmit,
        },
      })
    handleClose()
  }

  const disabled =
    !hourInterval?.start || !hourInterval?.end || !selectedDays?.length

  const id = "broadcast-hours-dialog"

  return (
    <DG.Dialog open={open} onClose={handleClose} id={id}>
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>{t("packages.broadcastHours")}</DG.DialogTitle>
        <DG.DialogClose onClose={handleClose} />
      </DG.DialogHeader>
      <DG.DialogContent paddingTop className={styles.dialogContent}>
        <div className={styles.daysSections}>
          <div className={styles.section}>
            {t("packages.broadcastHours.daysOfWeek")}
          </div>
          <div className={styles.daysContainer}>
            <DaysOfWeek
              isDaySelected={isDaySelected}
              onDayChange={onDayChange}
              disabled={!allowed}
            />
          </div>
        </div>
        <div className={styles.beltSection}>
          <div className={styles.section}>
            {t("packages.broadcastHours.timeBelt")}
          </div>
          <div className={styles.hourOptions}>
            <HourRangePicker
              start={hourInterval?.start || ""}
              end={hourInterval?.end || ""}
              onChange={onTimeBeltChange}
              disabled={!allowed}
            />
          </div>
        </div>
        <Exclusions
          exclusions={selectedExclusions}
          onExclusionsChange={onExclusionsChange}
          onExclusionsRemove={onExclusionsRemove}
          onExclusionsCheck={onExclusionsCheck}
          selectedDays={selectedDays}
          allowed={allowed}
        />
      </DG.DialogContent>
      {allowed ? (
        <DG.DialogFooter>
          <DG.DialogActions>
            <Button variant="outlined" color="primary" onClick={handleClose}>
              {t("actions.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              form="new-package"
              onClick={handleSubmit}
              disabled={disabled}
            >
              {t("actions.done")}
            </Button>
          </DG.DialogActions>
        </DG.DialogFooter>
      ) : null}
    </DG.Dialog>
  )
}
function BroadcastHoursCell(
  props: CellProps<Partial<PackageItem>> & { allowed?: boolean },
) {
  const { row, name, getValue, onUpdate, rowId, allowed } = props
  const initialValue = getValue ? getValue(row) : get(row, name, {})
  const [open, setOpen] = useState<boolean>(false)
  const handleClose = () => {
    setOpen(false)
  }
  return (
    <>
      <div style={{ width: "100%" }}>
        <div onClick={() => setOpen(true)}>
          {"Broadcast hours"}
          <IconButton className={styles.icon}>
            <PriceTableIcon />
          </IconButton>
        </div>
        <div>
          <BroadcastHoursModal
            onClose={handleClose}
            data={initialValue}
            onUpdate={onUpdate}
            rowId={rowId}
            open={open}
            allowed={allowed}
          />
        </div>
      </div>
    </>
  )
}

function SelectCell<T extends Record<string, any>>(
  props: CellProps<T> & {
    vendorProducts?: {
      label: string
      value: string
    }[]
    allowed?: boolean
  },
) {
  const {
    row,
    rowId,
    name,
    getValue,
    getOutput,
    onUpdate,
    vendorProducts,
    allowed,
  } = props
  const initialValue = getValue ? getValue(row) : get(row, name, "")
  const [val, setVal] = useState<string>(initialValue)

  const onBlur: FocusEventHandler<HTMLInputElement> = () => {
    const output = getOutput ? getOutput(val) : ({ [name]: val } as Partial<T>)
    onUpdate(rowId, output)
  }

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setVal(e.target.value)
    const output = getOutput
      ? getOutput(val)
      : ({ [name]: e.target.value } as Partial<T>)
    rowId === "NEW_ROW" && onUpdate("", output)
  }

  const selectedLabel = vendorProducts?.find((p) => p?.value === val)?.label

  return allowed ? (
    <div className={styles.buttonContainer}>
      <Options
        name={name as string}
        value={val}
        options={vendorProducts || []}
        onChange={onChange}
        onBlur={onBlur}
        size="medium"
        style={{ width: "200px" }}
      />
    </div>
  ) : (
    <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
      {selectedLabel}
    </div>
  )
}

function EditableCell<T extends Record<string, any>>(
  props: CellProps<T> & {
    allowed?: boolean
    clearErrors?: (rowId: string, name: string) => void
  },
) {
  const {
    row,
    rowId,
    name,
    getValue,
    getOutput,
    getError,
    onUpdate,
    allowed,
    clearErrors,
  } = props
  const error = getError?.(rowId, name) ?? ""

  const initialValue = getValue ? getValue(row) : get(row, name, "")
  const [currentValue, setCurrentValue] = useState(initialValue)

  const onBlur: FocusEventHandler<HTMLInputElement> = (evt) => {
    const value = evt.target.value
    const hasValueDiff = currentValue.toString() !== value
    const output = getOutput
      ? getOutput(value)
      : ({ [name]: value } as Partial<T>)
    hasValueDiff && onUpdate(rowId, output)
    setCurrentValue(value)
    clearErrors && error && clearErrors(rowId, name as string)
  }
  const onChange: FocusEventHandler<HTMLInputElement> = (evt) => {
    const value = evt.target.value
    const output = getOutput
      ? getOutput(value)
      : ({ [name]: value } as Partial<T>)
    rowId === "NEW_ROW" && onUpdate("", output)
  }

  const placeholder = "Add " + (name as string) + "..."
  return allowed ? (
    <GhostTextInput
      defaultValue={initialValue}
      placeholder={placeholder}
      onChange={onChange}
      onBlur={onBlur}
      className={cx(styles.input, styles.ghostInput, {
        [styles.error]: !!error,
      })}
      disabled={!allowed}
    />
  ) : (
    <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
      {initialValue}
    </div>
  )
}

const styles = {
  beltSection: css`
    font-size: 14px;
    color: var(--black);
    margin-bottom: 32px;
    display: flex;
  `,
  section: css`
    width: 115px;
    align-self: center;
  `,
  hourOptions: css`
    display: flex;
    padding-left: var(--spacing-xs);
  `,
  daysSections: css`
    font-size: 14px;
    color: var(--black);
    margin-bottom: 27px;
    display: flex;
  `,
  shape: css`
    width: 15px;
    height: 15px;
    background-color: var(--primary-dark);
    border-radius: 50%;
    color: var(--white);
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;
    cursor: pointer;
    &:hover {
      opacity: 0.8;
    }
  `,
  deleteIcon: css`
    width: 24px;
    height: 24px;
    display: flex;
    align-self: center;
  `,
  checkboxCol: css`
    display: flex;
    align-items: center;
  `,
  checkbox: css`
    padding: 6px;
  `,
  daysContainer: css`
    padding-left: var(--spacing-xs);
  `,
  days: css`
    font-size: 14px;
    color: var(--black);
    align-items: center;
    display: flex;
    margin-bottom: 12px;
    justify-content: space-between;
    width: 100%;
    padding-right: 56px;
  `,
  switchSection: css`
    font-size: 14px;
    color: var(--black);
    margin-bottom: 38px;
  `,
  dialogContent: css`
    padding-bottom: 38px;
  `,
  input: css`
    font-size: 13px;
    :hover {
      cursor: pointer;
    }
  `,
  halfInput: css`
    width: 50%;
  `,
  ghostInput: css`
    :focus-within {
      border: 2px solid var(--primary-main, teal);
    }
  `,
  ghostSelect: css`
    align-items: center;
    border: none;

    outline: none;

    .MuiSvgIcon-root {
      display: none;
    }

    :not([data-disabled="true"]):hover {
      border-color: var(--primary-main, teal);
      .MuiSvgIcon-root {
        display: block;
      }
    }

    :focus,
    :focus-within {
      border-color: var(--primary-main, teal);
      .MuiSvgIcon-root {
        display: block;
      }
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    .MuiSelect-outlined.MuiSelect-outlined {
      padding-top: var(--spacing-xs);
      padding-bottom: var(--spacing-xs);
    }
  `,
  buttonContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  buttonHover: css`
    &:hover {
      border: 1px solid var(--primary-main);
    }
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 14px;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-active: transparent;
    --btn-bg-focus-ring-color: transparent;
    padding-left: var(--spacing-m);
    padding-right: var(--spacing-m);
    width: fit-content;
    &:focus {
      box-shadow: none;
    }
    &:hover {
      background-color: transparent;
      border: 1px solid var(--primary-main);
    }
  `,
  hover: css`
    color: var(--primary-main);
    cursor: pointer;
    font-size: 13px;
    margin-left: 4px;
    margin-right: var(--spacing-m);
  `,
  popover: css`
    pointer-events: none;
  `,
  paper: css`
    padding: 1rem;
    display: flex;
    flex-direction: column;
  `,
  popoverList: css`
    font-size: 13px;
  `,
  primary: css`
    color: var(--primary-main);
  `,
  error: css`
    border: 1px solid var(--red);
  `,
  disabledText: css`
    font-size: 13px;
    color: var(--black);
    text-align: center;
  `,
  options: {
    short: css`
      color: var(--black);
      font-size: 0.8125rem;
      min-width: 2.5rem;
    `,
    label: css`
      color: var(--dark-grey);
      font-size: 0.8125rem;
      margin-left: 0.1rem;
    `,
  },
  icon: css`
    --btn-padding: var(--spacing-xxs);
    --btn-min-width: var(--spacing-xxl-2-1);
    &:focus {
      box-shadow: none;
    }
  `,
  readonly: css`
    margin: var(--spacing-m);
  `,
}

export const columns = (
  vendorProducts?: {
    label: string
    value: string
  }[],
  allowed?: boolean,
  clearErrors?: (rowId: string, name: string) => void,
): Col<Partial<PackageItem>>[] => [
  {
    id: "Name",
    name: "name",
    cell: (props) => <EditableCell {...props} allowed={allowed} />,
    width: "15%",
    existsInCreate: true,
  },
  {
    id: "Vendor Product",
    name: "supplierProductId",
    cell: (props) => (
      <SelectCell
        {...props}
        vendorProducts={vendorProducts}
        allowed={allowed}
      />
    ),
    existsInCreate: true,
    required: true,
    width: "5%",
  },
  {
    id: "Broadcast Hours",
    name: "broadcastHours",
    width: "25%",
    cell: (props) => <BroadcastHoursCell {...props} allowed={allowed} />,
    existsInCreate: false,
    align: "center",
  },
  {
    id: "No of Spots",
    name: "numberOfSpots",
    width: "15%",
    cell: (props) => (
      <EditableCell {...props} allowed={allowed} clearErrors={clearErrors} />
    ),
    existsInCreate: false,
  },
  {
    id: "Base Rate",
    name: "baseRate",
    width: "15%",
    cell: (props) => (
      <EditableCell {...props} allowed={allowed} clearErrors={clearErrors} />
    ),
    existsInCreate: false,
  },
  {
    id: "Value",
    name: "value",
    width: "15%",
    cell: (props) => (
      <EditableCell {...props} allowed={allowed} clearErrors={clearErrors} />
    ),
    existsInCreate: false,
  },
]
